import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import ClickLayer from '../ClickLayer/ClickLayer.js';
import Image from '../Image/Image.js';
import Translate from '../Translate/Translate.js';
import { TranslationContext } from '../Translate/Translate.js';

import DatePickerInput from './components/DatePickerInput/index.js';
import DatePickerMonth from './components/DatePickerMonth/index.js';
import { IDatePickerConfiguration, IDatePickerProps, IDatePickerState } from './models/index.js';
import ButtonClose from './styledComponents/ButtonClose.js';
import ButtonNext from './styledComponents/ButtonNext.js';
import ButtonPrevious from './styledComponents/ButtonPrevious.js';
import DatePickerWrapper from './styledComponents/DatePickerWrapper.js';
import Dialog from './styledComponents/Dialog.js';
import DialogContent from './styledComponents/DialogContent.js';
import Header from './styledComponents/Header.js';
import Mobile from './styledComponents/Mobile.js';
import MobileHeading from './styledComponents/MobileHeading.js';
import NavigationWrapper from './styledComponents/NavigationWrapper.js';
import SelectLabel from './styledComponents/SelectLabel.js';
import SelectMonth from './styledComponents/SelectMonth.js';
import SelectWrapper from './styledComponents/SelectWrapper.js';
import SelectYear from './styledComponents/SelectYear.js';
import { datePickerStyle } from './styles/DatePicker.style.js';
import { DATE_PICKER_CATEGORY_KEY, DATE_PICKER_COMPONENT_KEY } from './DatePicker.meta.js';

const {
	addDays,
	clamp,
	createDate,
	createIdentifier,
	endOfMonth,
	endOfWeek,
	inRange,
	parseISODate,
	printISODate,
	setMonth,
	setYear,
	startOfMonth,
	startOfWeek,
} = common.commonUtils;

const DISALLOWED_CHARACTERS = /[^0-9./-]+/g;
const TRANSITION_MS = 300;

function range(from: number, to: number) {
	const result: number[] = [];
	for (let i = from; i <= to; i++) {
		result.push(i);
	}
	return result;
}

const keyCode = {
	TAB: 9,
	ESC: 27,
	SPACE: 32,
	PAGE_UP: 33,
	PAGE_DOWN: 34,
	END: 35,
	HOME: 36,
	LEFT: 37,
	UP: 38,
	RIGHT: 39,
	DOWN: 40,
};

function cleanValue(input: HTMLInputElement, regex: RegExp): string {
	const value = input.value || '';
	const cursor = input.selectionStart || undefined;

	const beforeCursor = value.slice(0, cursor);
	const afterCursor = value.slice(cursor, value.length);

	const filteredBeforeCursor = beforeCursor.replace(regex, '');
	const filterAfterCursor = afterCursor.replace(regex, '');

	const newValue = filteredBeforeCursor + filterAfterCursor;
	const newCursor = filteredBeforeCursor.length;

	input.value = newValue;
	input.selectionStart = input.selectionEnd = newCursor;

	return newValue;
}

export class DatePicker extends ComponentBase<IDatePickerProps, IDatePickerState, IDatePickerConfiguration> {
	categoryKey = DATE_PICKER_CATEGORY_KEY;
	componentKey = DATE_PICKER_COMPONENT_KEY;

	/**
	 * observable variables
	 */
	activeFocus = false;
	focusedDay = new Date();
	open = false;
	value: string;
	valueAsDate: Date | undefined;

	/**
	 * Own Properties
	 */
	private monthSelectId = createIdentifier('KsysDateMonth');
	private yearSelectId = createIdentifier('KsysDateYear');
	private dialogLabelId = createIdentifier('KsysDateLabel');

	private datePickerButton: any | null | undefined;
	private datePickerInput: any | null | undefined;
	private firstFocusableElement: any | null | undefined;
	private monthSelectNode: any | null | undefined;
	private dialogWrapperNode: any | null | undefined;

	// Doesn't need a createRef as focusedDayNode gets set on keyboard navigation
	private focusedDayNode: any | null | undefined;

	private focusTimeoutId: ReturnType<typeof setTimeout> | undefined;

	private initialTouchX: number | null = null;
	private initialTouchY: number | null = null;

	/**
	 * Whilst dateAdapter is used for handling the formatting/parsing dates in the input,
	 * these are used to format dates exclusively for the benefit of screen readers.
	 *
	 * We prefer DateTimeFormat over date.toLocaleDateString, as the former has
	 * better performance when formatting large number of dates. See:
	 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Performance
	 */
	private dateFormatShort: Intl.DateTimeFormat = new Intl.DateTimeFormat();
	private dateFormatLong: Intl.DateTimeFormat = new Intl.DateTimeFormat();

	constructor(props: IDatePickerProps) {
		super(props);

		this.datePickerButton = React.createRef();
		this.datePickerInput = React.createRef();
		this.firstFocusableElement = React.createRef();
		this.monthSelectNode = React.createRef();
		this.dialogWrapperNode = React.createRef();

		// this.handleDocumentClick = this.handleDocumentClick.bind(this);
		// addEventListener('click', this.handleDocumentClick);
		this.value = props.value;
		this.valueAsDate = parseISODate(props.value);
		this.createDateFormatters();

		makeObservable<
			DatePicker,
			| 'enableActiveFocus'
			| 'disableActiveFocus'
			| 'setFocusedDay'
			| 'handleInputChange'
			| 'setValue'
			| 'processFocusedDayNode'
		>(this, {
			activeFocus: observable,
			focusedDay: observable.ref,
			open: observable,
			value: observable,
			valueAsDate: observable.ref,
			createDateFormatters: action,
			show: action,
			hide: action,
			enableActiveFocus: action,
			disableActiveFocus: action,
			setFocusedDay: action,
			handleInputChange: action,
			setValue: action,
			processFocusedDayNode: action,
		});
	}

	componentDidUpdate = (prev: IDatePickerProps) => {
		if (prev.value !== this.props.value) {
			runInAction(() => {
				this.value = this.props.value;
				this.valueAsDate = parseISODate(this.value);
			});
		}
	};

	createDateFormatters = () => {
		const { localization, dateFormatShort, dateFormatLong } = this.props;
		this.dateFormatShort = new Intl.DateTimeFormat(
			localization.locale,
			dateFormatShort || { day: 'numeric', month: 'long' },
		);
		this.dateFormatLong = new Intl.DateTimeFormat(
			localization.locale,
			dateFormatLong || {
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			},
		);
	};

	/**
	 * Public methods API
	 */

	/**
	 * Sets focus on the date picker's input. Use this method instead of the global `focus()`.
	 */
	setFocus = async () => {
		return this.datePickerInput && this.datePickerInput.current.focus();
	};

	/**
	 * Show the calendar modal, moving focus to the calendar inside.
	 */
	show = async () => {
		this.open = true;

		this.setFocusedDay(parseISODate(this.value) || new Date());

		if (this.focusTimeoutId) {
			clearTimeout(this.focusTimeoutId);
		}
		this.focusTimeoutId = setTimeout(
			() => this.monthSelectNode && this.monthSelectNode.current.focus(),
			TRANSITION_MS,
		);
	};

	/**
	 * Hide the calendar modal. Set `moveFocusToButton` to false to prevent focus
	 * returning to the date picker's button. Default is true.
	 */
	hide = async (moveFocusToButton = true) => {
		this.open = false;

		// in cases where calendar is quickly shown and hidden
		// we should avoid moving focus to the button
		if (this.focusTimeoutId) {
			clearTimeout(this.focusTimeoutId);
		}

		if (moveFocusToButton) {
			// iOS VoiceOver needs to wait for all transitions to finish.
			setTimeout(() => this.datePickerButton && this.datePickerButton.current.focus(), TRANSITION_MS + 200);
		}
	};

	/**
	 * Local methods.
	 */
	private enableActiveFocus = () => {
		this.activeFocus = true;
	};

	private disableActiveFocus = () => {
		this.activeFocus = false;
	};

	private addDays(days: number) {
		this.setFocusedDay(addDays(this.focusedDay, days));
	}

	private addMonths(months: number) {
		this.setMonth(this.focusedDay.getMonth() + months);
	}

	private addYears(years: number) {
		this.setYear(this.focusedDay.getFullYear() + years);
	}

	private startOfWeek = () => {
		this.setFocusedDay(startOfWeek(this.focusedDay, this.props.firstDayOfWeek));
	};

	private endOfWeek = () => {
		this.setFocusedDay(endOfWeek(this.focusedDay, this.props.firstDayOfWeek));
	};

	private setMonth(month: number) {
		const min = setMonth(startOfMonth(this.focusedDay), month);
		const max = endOfMonth(min);
		const date = setMonth(this.focusedDay, month);

		this.setFocusedDay(clamp(date, min, max));
	}

	private setYear(year: number) {
		const min = setYear(startOfMonth(this.focusedDay), year);
		const max = endOfMonth(min);
		const date = setYear(this.focusedDay, year);

		this.setFocusedDay(clamp(date, min, max));
	}

	private setFocusedDay = (day: Date) => {
		const { min, max } = this.props;
		this.focusedDay = clamp(day, parseISODate(min), parseISODate(max));
	};

	private toggleOpen = (e: Event) => {
		e.preventDefault();
		this.open ? this.hide(false) : this.show();
	};

	private handleEscKey = (event: any): any => {
		if (event.keyCode === keyCode.ESC) {
			this.hide();
		}
	};

	private handleBlur = (event: Event): any => {
		event.stopPropagation();
	};

	private handleFocus = (event: Event): any => {
		event.stopPropagation();
	};

	// TODO: Implement touch events for mobile
	// @action
	// private handleTouchStart = (event: TouchEvent): any => {
	// 	const touch = event.changedTouches[0];
	// 	this.initialTouchX = touch.pageX;
	// 	this.initialTouchY = touch.pageY;
	// }

	// private handleTouchMove = (event: TouchEvent): any => {
	// 	event.preventDefault();
	// }

	// @action
	// private handleTouchEnd = (event: TouchEvent): any => {
	// 	const touch = event.changedTouches[0];
	// 	const distX = touch.pageX - (this.initialTouchX || 0); // get horizontal dist traveled
	// 	const distY = touch.pageY - (this.initialTouchY || 0); // get vertical dist traveled
	// 	const threshold = 70;

	// 	const isHorizontalSwipe = Math.abs(distX) >= threshold && Math.abs(distY) <= threshold;
	// 	const isDownwardsSwipe = Math.abs(distY) >= threshold && Math.abs(distX) <= threshold && distY > 0;

	// 	if (isHorizontalSwipe) {
	// 		this.addMonths(distX < 0 ? 1 : -1);
	// 	}
	// 	else if (isDownwardsSwipe) {
	// 		this.hide(false);
	// 		event.preventDefault();
	// 	}

	// 	this.initialTouchY = null;
	// 	this.initialTouchX = null;
	// }

	private handleNextMonthClick = (event: any): any => {
		event.preventDefault();
		this.addMonths(1);
	};

	private handlePreviousMonthClick = (event: any): any => {
		event.preventDefault();
		this.addMonths(-1);
	};

	private handleFirstFocusableKeydown = (event: any): any => {
		// this ensures focus is trapped inside the dialog
		if (event.keyCode === keyCode.TAB && event.shiftKey && this.focusedDayNode) {
			this.focusedDayNode.focus();
			event.preventDefault();
		}
	};

	private handleKeyboardNavigation = (event: KeyboardEvent): any => {
		// handle tab separately, since it needs to be treated
		// differently to other keyboard interactions
		if (event.keyCode === keyCode.TAB && !event.shiftKey && this.firstFocusableElement) {
			event.preventDefault();
			this.firstFocusableElement.current.focus();
			return;
		}

		let handled = true;

		switch (event.keyCode) {
			case keyCode.RIGHT:
				this.addDays(1);
				break;
			case keyCode.LEFT:
				this.addDays(-1);
				break;
			case keyCode.DOWN:
				this.addDays(7);
				break;
			case keyCode.UP:
				this.addDays(-7);
				break;
			case keyCode.PAGE_UP:
				if (event.shiftKey) {
					this.addYears(-1);
				} else {
					this.addMonths(-1);
				}
				break;
			case keyCode.PAGE_DOWN:
				if (event.shiftKey) {
					this.addYears(1);
				} else {
					this.addMonths(1);
				}
				break;
			case keyCode.HOME:
				this.startOfWeek();
				break;
			case keyCode.END:
				this.endOfWeek();
				break;
			default:
				handled = false;
		}

		if (handled) {
			event.preventDefault();
			this.enableActiveFocus();
		}
	};

	private handleDaySelect = (e: MouseEvent, day: Date) => {
		const { min, max, isDateDisabled } = this.props;
		const isInRange = inRange(day, parseISODate(min), parseISODate(max));
		const isAllowed = !isDateDisabled(day);

		if (isInRange && isAllowed) {
			this.setValue(day);
			this.hide();
		} else {
			// for consistency we should set the focused day in cases where
			// user has selected a day that has been specifically disallowed
			this.setFocusedDay(day);
		}
	};

	private handleMonthSelect = (e: any) => {
		if (e.target) {
			this.setMonth(parseInt(e.target.value, 10));
		}
	};

	private handleYearSelect = (e: any) => {
		if (e.target) {
			this.setYear(parseInt(e.target.value, 10));
		}
	};

	private handleInputChange = () => {
		const { dateAdapter } = this.props;
		const target = this.datePickerInput.current;

		if (target) {
			// clean up any invalid characters
			cleanValue(target, DISALLOWED_CHARACTERS);

			const parsed = dateAdapter.parse(target.value, createDate);
			if (parsed) {
				this.setValue(parsed);
			}
		}
	};

	private setValue(date: Date) {
		this.value = printISODate(date);
		this.valueAsDate = parseISODate(this.value);
		const { onChange } = this.props;
		if (onChange) {
			onChange(this.value);
		}
	}

	private processFocusedDayNode = (element: any) => {
		this.focusedDayNode = element;
		if (this.activeFocus && this.open) {
			setTimeout(() => element.focus(), 0);
		}
	};

	render() {
		const {
			className,
			dateAdapter,
			min,
			max,
			direction,
			localization,
			name = 'date',
			disabled,
			role,
			required,
			identifier,
			firstDayOfWeek,
			labelId,
			isDateDisabled,
		} = this.props;
		const formattedDate = this.valueAsDate && dateAdapter.format(this.valueAsDate);
		const selectedYear = (this.valueAsDate || this.focusedDay).getFullYear();
		const focusedMonth = this.focusedDay.getMonth();
		const focusedYear = this.focusedDay.getFullYear();

		const minDate = parseISODate(min);
		const maxDate = parseISODate(max);
		const prevMonthDisabled =
			minDate != null && minDate.getMonth() === focusedMonth && minDate.getFullYear() === focusedYear;
		const nextMonthDisabled =
			maxDate != null && maxDate.getMonth() === focusedMonth && maxDate.getFullYear() === focusedYear;

		const minYear = minDate ? minDate.getFullYear() : selectedYear - 10;
		const maxYear = maxDate ? maxDate.getFullYear() : selectedYear + 10;
		return (
			<DatePickerWrapper className={'date-picker ' + className}>
				<DatePickerInput
					dateFormatter={this.dateFormatLong}
					value={this.value}
					valueAsDate={this.valueAsDate}
					formattedValue={formattedDate}
					onInput={this.handleInputChange}
					onBlur={this.handleBlur}
					onFocus={this.handleFocus}
					onClick={this.toggleOpen}
					name={name}
					disabled={disabled}
					role={role}
					required={required}
					identifier={identifier}
					localization={localization}
					buttonRef={this.datePickerButton}
					inputRef={this.datePickerInput}
					labelId={labelId}
				/>

				{this.open && <ClickLayer onClick={() => this.hide(false)} />}
				<Dialog
					className={`${direction === 'left' && 'is-left'} ${this.open && 'is-active'}`}
					role="dialog"
					aria-modal="true"
					aria-hidden={this.open ? 'false' : 'true'}
					aria-labelledby={this.dialogLabelId}
					// onTouchMove={this.handleTouchMove}
					// onTouchStart={this.handleTouchStart}
					// onTouchEnd={this.handleTouchEnd}
				>
					<DialogContent
						className={`dialog-content ${this.open && 'is-active'}`}
						onKeyDown={this.handleEscKey}
						innerRef={this.dialogWrapperNode}
					>
						<Mobile className="mobile" onFocus={this.disableActiveFocus}>
							<MobileHeading className="mobile-heading">
								<Translate>{localization.calendarHeading}</Translate>
							</MobileHeading>
							<ButtonClose
								className="close"
								innerRef={this.firstFocusableElement}
								onKeyDown={this.handleFirstFocusableKeydown}
								onClick={() => this.hide()}
								type="button"
							>
								<Image aria-hidden={true} asset={'base.datePicker.close'} />
								<span className="vhidden">
									<Translate>{localization.closeLabel}</Translate>
								</span>
							</ButtonClose>
						</Mobile>
						<Header className="header" onFocus={this.disableActiveFocus}>
							<div>
								<h2 id={this.dialogLabelId} className="vhidden" aria-live="polite" aria-atomic="true">
									<Translate>{localization.monthNames[focusedMonth]}</Translate>{' '}
									{this.focusedDay.getFullYear()}
								</h2>
								<label htmlFor={this.monthSelectId} className="vhidden">
									<Translate>{localization.monthSelectLabel}</Translate>
								</label>
								<SelectWrapper className="select">
									<SelectMonth
										id={this.monthSelectId}
										value={focusedMonth}
										className="select--month"
										innerRef={this.monthSelectNode}
										onChange={this.handleMonthSelect}
									>
										<TranslationContext.Consumer>
											{(translationContext) => {
												const { translate } = translationContext;
												return localization.monthNames.map((month, i) => {
													const translatedMonth = translate(month);
													return (
														<option
															key={translatedMonth}
															value={i}
															disabled={
																!inRange(
																	new Date(focusedYear, i, 1),
																	minDate ? startOfMonth(minDate) : undefined,
																	maxDate ? endOfMonth(maxDate) : undefined,
																)
															}
														>
															{translatedMonth}
														</option>
													);
												});
											}}
										</TranslationContext.Consumer>
									</SelectMonth>
									<SelectLabel className="select-label" aria-hidden="true">
										<span>
											<Translate>{localization.monthNamesShort[focusedMonth]}</Translate>
										</span>
										<Image aria-hidden={true} asset="base.datePicker.dropdown"></Image>
									</SelectLabel>
								</SelectWrapper>

								<label htmlFor={this.yearSelectId} className="vhidden">
									<Translate>{localization.yearSelectLabel}</Translate>
								</label>
								<SelectWrapper className="select">
									<SelectYear
										id={this.yearSelectId}
										value={focusedYear}
										className="select--year"
										onChange={this.handleYearSelect}
									>
										{range(minYear, maxYear).map((year) => (
											<option key={year}>{year}</option>
										))}
									</SelectYear>
									<SelectLabel className="select-label" aria-hidden="true">
										<span>{this.focusedDay.getFullYear()}</span>
										<Image aria-hidden={true} asset="base.datePicker.dropdown"></Image>
									</SelectLabel>
								</SelectWrapper>
							</div>

							<NavigationWrapper className="nav">
								<ButtonPrevious
									className="prev"
									onClick={this.handlePreviousMonthClick}
									disabled={prevMonthDisabled}
									type="button"
								>
									<Image aria-hidden={true} asset="base.datePicker.previousMonth"></Image>
									<span className="vhidden">
										<Translate>{localization.prevMonthLabel}</Translate>
									</span>
								</ButtonPrevious>
								<ButtonNext
									className="next"
									onClick={this.handleNextMonthClick}
									disabled={nextMonthDisabled}
									type="button"
								>
									<Image aria-hidden={true} asset="base.datePicker.nextMonth"></Image>
									<span className="vhidden">
										<Translate>{localization.nextMonthLabel}</Translate>
									</span>
								</ButtonNext>
							</NavigationWrapper>
						</Header>
						<DatePickerMonth
							dateFormatter={this.dateFormatShort}
							selectedDate={this.valueAsDate}
							focusedDate={this.focusedDay}
							onDateSelect={this.handleDaySelect}
							onKeyboardNavigation={this.handleKeyboardNavigation}
							labelledById={this.dialogLabelId}
							localization={localization}
							firstDayOfWeek={firstDayOfWeek}
							focusedDayRef={this.processFocusedDayNode}
							min={minDate}
							max={maxDate}
							isDateDisabled={isDateDisabled}
						/>
					</DialogContent>
				</Dialog>
			</DatePickerWrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(DatePicker),
	DATE_PICKER_CATEGORY_KEY,
	DATE_PICKER_COMPONENT_KEY,
	undefined,
	undefined,
	undefined,
	datePickerStyle,
);
